import {useCallback} from 'react';
import _ from 'lodash';

export async function generateWebVTT(json, isOriginalText = true, fileType = "text/srt") {
    const cues = json.map((cue, index) => {
        const start = cue.timestamp.start.replace(".", ",");
        const end = cue.timestamp.end.replace(".", ",");
        const text = isOriginalText ? cue.original_text || '' : cue.translated_text || '';

        return `${index + 1}\n${start} --> ${end}\n${text}\n`;
    }).join("\n");

    // Create Blob and Object URL
    const blob = await new Blob([cues], {type: fileType || "text/srt"});
    return URL.createObjectURL(blob);
}


export function timestampToSeconds(timestamp = "00:00:00") {
    const [hours, minutes, seconds] = timestamp?.split(':');
    //const [seconds, milliseconds] = secondsAndMilliseconds.split(',');

    return _.round((parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60 + parseInt(seconds, 10)))
}

export function timestampToSecondsMs(timestamp = "00:00:00") {
    const [hours, minutes, secondsAndMilliseconds] = timestamp?.split(':');
    const [seconds, milliseconds] = secondsAndMilliseconds.split(',');

    return _.round((parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60 + parseInt(seconds, 10) + parseInt((milliseconds || 0), 10) / 1000) || 0, 2);
}

export function secondsToTimestamp(seconds = 0) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return [
        String(hours).padStart(2, '0'),
        String(minutes).padStart(2, '0'),
        String(secs).padStart(2, '0'),
    ].join(':');
}


export function secondsToTimestampWithMs(seconds = 0) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);

    return [
        String(hours).padStart(2, '0'),
        String(minutes).padStart(2, '0'),
        String(secs).padStart(2, '0') + ',' + String(milliseconds).padStart(3, '0'),
    ].join(':');
}

export function useUpdateFormValue(onValueChange) {
    // Use useCallback to memoize the updateValue function
    return useCallback(
        (field, value) => {
            if (typeof onValueChange === "function") {
                onValueChange(field, value);
            } else {
                console.warn("onValueChange is not a function");
            }
        },
        [onValueChange] // Dependency array
    );
}

export const parseTimeToMs = (time = "00:00:00,000") => {
    const [hours, minutes, sec] = _.split(time, ':');
    //const [sec, ms] = _.split(seconds, ',');
    return (
        parseInt(hours, 10) * 60 * 60 * 1000 +
        parseInt(minutes, 10) * 60 * 1000 +
        parseInt(sec, 10) * 1000
        //+
        //parseInt(ms, 10)
    );
};

const fetchAndCompressAudio = async (audioUrl) => {
    if (!audioUrl) throw new Error('No audio URL provided');

    // Fetch audio file as a Blob
    const response = await fetch(audioUrl);
    const originalBlob = await response.blob();

    // Convert Blob to AudioBuffer using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await originalBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Downsample (Reduce sample rate for compression)
    const offlineContext = new OfflineAudioContext({
        numberOfChannels: audioBuffer.numberOfChannels,
        length: audioBuffer.length / 2, // Reduce length by half (basic compression)
        sampleRate: 22050, // Lower sample rate (default is 44100)
    });

    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineContext.destination);
    source.start();

    // Render compressed audio
    const renderedBuffer = await offlineContext.startRendering();
    const compressedBlob = new Blob([renderedBuffer], {type: 'audio/wav'});

    // Create Object URL for playback
    return URL.createObjectURL(compressedBlob);
};

function formatDuration(durationInSeconds) {
    const totalSeconds = _.round(durationInSeconds); // Round to nearest second
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return hours > 0
        ? _.padStart(hours, 2, '0') + ':' + _.padStart(minutes, 2, '0') + ':' + _.padStart(seconds, 2, '0')
        : _.padStart(minutes, 2, '0') + ':' + _.padStart(seconds, 2, '0');
}

const formatIndianNumber = (num) => {
    // Convert number to string using lodash
    const numStr = _.toString(num);

    // Separate the last three digits
    const lastThree = numStr.slice(-3);
    const otherNumbers = numStr.slice(0, numStr.length - 3);

    // Format the digits before the last three into groups of two using a regex
    let formattedOther = otherNumbers;
    if (otherNumbers) {
        formattedOther = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
    }

    // Concatenate the formatted parts
    return formattedOther ? `${formattedOther},${lastThree}` : numStr;
};

function filterArrayContent(array, query, accessor = (item) => item) {
    if (!query) return array;

    const lowerQuery = _.toLower(query);

    return _.filter(array, (item) => {
        const content = _.toLower(_.toString(accessor(item)));
        return content.includes(lowerQuery) || item?.is_cloned === true;
    });
}


export function getSegmentColorWithData(segmentDurationInSeconds = 0, audioDurationInSeconds = 0) {
    const diff = audioDurationInSeconds - segmentDurationInSeconds;
    const diffPercent = Math.abs((diff / segmentDurationInSeconds) * 100);
    const audioSec = (audioDurationInSeconds || 1).toFixed(1);
    const segmentSec = (segmentDurationInSeconds || 1).toFixed(1);
    const direction = diff > 0 ? "longer" : "shorter";
    const arrow = diff > 0 ? "▲" : "▼";

    let icon = "🟢";
    let color = '#b7fb96' || "#A0AEC0";
    let label = "Perfect match";
    let impact = "✓ Duration matches – no issues";

    if (diffPercent > 5 && diffPercent <= 10) {
        icon = "🟩";
        color = "#D0F0C0";
        label = "Slight variation";
        impact = `${arrow} Audio is ${direction} by ${diffPercent.toFixed(1)}% – acceptable`;
    } else if (diffPercent > 10 && diffPercent <= 20) {
        icon = "🟨";
        color = "#FFF59D";
        label = "Minor mismatch";
        impact = `${arrow} Audio is ${direction} by ${diffPercent.toFixed(1)}% – usually safe`;
    } else if (diffPercent > 20 && diffPercent <= 30) {
        icon = "🟧";
        color = "#FFB74D";
        label = "Moderate mismatch";
        impact = `${arrow} Audio is ${direction} by ${diffPercent.toFixed(1)}% – review suggested`;
    } else if (diffPercent > 30 && diffPercent <= 40) {
        icon = "🟥";
        color = "#FB8C00";
        label = "Off-timing";
        impact = `${arrow} Audio is ${direction} by ${diffPercent.toFixed(1)}% – likely playback issues`;
    } else if (diffPercent > 40 && diffPercent <= 60) {
        icon = "⬛";
        color = "#F44336";
        label = "Major mismatch";
        impact = `${arrow} Audio is ${direction} by ${diffPercent.toFixed(1)}% – may affect sync`;
    } else if (diffPercent > 60 && diffPercent <= 100) {
        icon = "⬛";
        color = "#C62828";
        label = "Severe mismatch";
        impact = `${arrow} Audio is ${direction} by ${diffPercent.toFixed(1)}% – segment likely unusable`;
    } else if (diffPercent > 100) {
        icon = "❌";
        color = "#6A1B9A";
        label = "Extreme duration error";
        impact = `${arrow} Audio is ${direction} by ${diffPercent.toFixed(1)}% – recheck segment boundaries`;
    }

    return {
        icon,
        color,
        label,
        diffPercent: diffPercent.toFixed(1) || 0,
        tooltip: `Segment: ${segmentSec} sec | Audio: ${audioSec} sec\t\n${impact}`
    };
}

/* Removes per-user session data on logout so the next account gets a fresh state (e.g. offer countdown) */
export function clearSessionData() {
    ['token', 'email', 'offerEndDate', 'offerEndInDays', 'freeTrialBannerLastShown'].forEach((key) => {
        localStorage.removeItem(key);
    });
}

export const appHelper = {
    formatDuration,
    fetchAndCompressAudio,
    formatIndianNumber,
    filterArrayContent
}

